import React, { useEffect } from 'react';
//@ts-ignore
import { AddressType, VehicleType } from '../../common/CommonTypes';
import FormInput from '../../common/FormInput';
import PanelWithButton from '../../common/panelWithButton/PanelWithButton';
import {
    CosmeticRepairRequestType,
    CosmeticPageErrors,
    CosmeticProcessStages
} from './CosmeticRepairRequestTypes';
import ErrorSummary from '../../common/ErrorSummary/ErrorSummary';
//@ts-ignore
import ToggleButton from '../../common/toggleButton/ToggleButton';
import { GoogleAnalytics } from '../../../classes/GoogleAnalytics';
import { TranslationHelper } from '../../../classes/TranslationHelper';
import { PageNames } from '../../../Constants/Common';
import ConfirmAlert from '../../common/confirmAlert/ConfirmAlert';
import { t } from 'i18next';

export type CosmeticRepairRequestSummaryProps = {
    cosmeticRepairRequests: CosmeticRepairRequestType[];
    selectedRepairAddress: AddressType;
    cosmeticRequestAmount: number;
    permissionGranted: boolean;
    errors?: CosmeticPageErrors;
    vehicle: VehicleType;
    showCancelAlert: boolean;
    cancelConfirmText: string;
    onSubmitCosmeticRepairRequests: () => void;
    onUpdateAddressClick: (scrollToAddress: boolean) => void;
    onBack: (CosmeticProcessStages) => void;
    onUpdateCosmeticClick: (requestId: number) => void;
    onTogglePermission: (isChecked: boolean) => void;
    onCancelClick: () => void;
    onCancelRepairRequest: () => void;
    onCancelUndo: () => void;
};
const CosmeticRepairRequestSummary = ({
    cosmeticRepairRequests,
    selectedRepairAddress,
    permissionGranted,
    errors,
    vehicle,
    showCancelAlert,
    cancelConfirmText,
    onSubmitCosmeticRepairRequests,
    cosmeticRequestAmount,
    onUpdateAddressClick,
    onBack,
    onUpdateCosmeticClick,
    onTogglePermission,
    onCancelClick,
    onCancelRepairRequest,
    onCancelUndo
}: CosmeticRepairRequestSummaryProps): JSX.Element => {
    useEffect(() => {
        GoogleAnalytics.setPageView(PageNames.CosmeticSummary);
    }, []);

    const onUpdateRepairAddress = () => {
        onBack(CosmeticProcessStages.RepairAddress);
        onUpdateAddressClick(true);
    };
    const errorlist = (): Array<string | undefined> => {
        let pageErrors = [errors?.permissionGranted];
        pageErrors = pageErrors.filter((error) => error !== undefined);
        return pageErrors;
    };
    const onBackButtonClick = () => {
        onBack(CosmeticProcessStages.RepairAddress);
        GoogleAnalytics.trackEventsGoogleAnalytics(
            'Button click',
            'Cosmetic request click',
            'Clicked back button on summary page'
        );
    };
    const forCancelButton = () => {
        const buttonText =
            cosmeticRepairRequests.length > 1
                ? t('cancel_repair_requests')
                : t('cancel_repair_request');
        return (
            <button
                data-testid='danger-button'
                className='btn btn-danger progress-button'
                onClick={onCancelClick}>
                {buttonText}
            </button>
        );
    };

    const cancelButton = forCancelButton();
    return (
        <>
            <div className='card page-content'>
                <div className='card-body page-content-body'>
                    <FormInput
                        id='summary-vehicle-forminput'
                        label={t('vehicle')}>
                        <div
                            id='vehicle-summary-container'
                            className='div-container-style'>
                            <section>
                                <b>{vehicle.vrn}</b>
                                <br />
                                {vehicle.make}
                                <br />
                                {vehicle.model}
                            </section>
                        </div>
                    </FormInput>
                </div>
                <div className='card-body page-content-body'>
                    <FormInput
                        id='summary-repair-address-forminput'
                        label={t('repair_address')}>
                        <PanelWithButton
                            id='address-summary-container'
                            buttonText={t('update')}
                            onButtonClick={onUpdateRepairAddress}>
                            <address>
                                {selectedRepairAddress.addressLine1 || ''}
                                <br />
                                {selectedRepairAddress.addressLine2 || ''}
                                <br />
                                {selectedRepairAddress.townOrCity || ''}
                                <br />
                                {selectedRepairAddress.region || ''}
                                <br />
                                {selectedRepairAddress.postcode || ''}
                                <br />
                                {selectedRepairAddress.country || ''}
                            </address>
                        </PanelWithButton>
                    </FormInput>
                </div>
                <div className='card-body page-content-body'>
                    <FormInput
                        id='wheels-summary-forminput'
                        label={t('repair_requests')}>
                        {cosmeticRepairRequests.map((repairRequest, index) => (
                            <PanelWithButton
                                id={`wheel-summary-container-${index.toString()}`}
                                buttonText={t('update')}
                                onButtonClick={() =>
                                    onUpdateCosmeticClick(repairRequest.id)
                                }
                                key={index}>
                                <b>{t('panel')}: </b>
                                {repairRequest.selectedPanels
                                    .map((p) =>
                                        TranslationHelper.getTranslatedText(
                                            p.toString()
                                        )
                                    )
                                    .join(' , ')}
                                <br />
                                <b>{t('type_of_damage')}: </b>
                                {TranslationHelper.getTranslatedText(
                                    repairRequest.damageType
                                )}
                                <br />
                                <b>{t('length_of_damage_label')}: </b>
                                {repairRequest.damageLength}
                            </PanelWithButton>
                        ))}
                    </FormInput>
                </div>
                <hr />
                <div className='card-body page-content-body'>
                    <FormInput
                        id='terms-form'
                        errorText={errors?.permissionGranted}>
                        <div className='col'>
                            <ToggleButton
                                value={permissionGranted}
                                onChange={onTogglePermission}
                                label={t('repair_permission')}
                            />
                        </div>
                    </FormInput>
                </div>
                <div className='card-footer page-content-footer'>
                    {errors?.permissionGranted && (
                        <ErrorSummary
                            title={t('summary_error')}
                            errors={errorlist()}
                            className='card-error'
                        />
                    )}
                </div>
            </div>
            <div className='bottom-navigation'>
                <button
                    className='btn btn-primary progress-button'
                    onClick={onSubmitCosmeticRepairRequests}>
                    {t('submit_cosmetic_request_approval', {
                        cosmeticRequestAmount
                    })}
                </button>
                <button
                    aria-label='back-button'
                    className='btn btn-secondary progress-button'
                    onClick={onBackButtonClick}>
                    {t('back')}
                </button>
                {cancelButton}
            </div>
            <ConfirmAlert
                showAlert={showCancelAlert}
                onConfirm={onCancelRepairRequest}
                onCancel={onCancelUndo}
                alertText={cancelConfirmText}
            />
        </>
    );
};

export default CosmeticRepairRequestSummary;
